import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

/**
 * Standalone Preview Page — battle-tested layered approach.
 *
 * 1. Try iframing the client's actual website (interactive).
 * 2. If the site sets X-Frame-Options / CSP frame-ancestors (most do),
 *    the iframe silently fails — we detect via timeout + load-event probe
 *    and swap to a static screenshot backdrop (WordPress mshots).
 * 3. Either way, the chat widget loads on this page (parent) and overlays
 *    fixed-position in the corner — visible above iframe AND screenshot.
 *
 * This is the same pattern Drift / Intercom / HubSpot use in their
 * dashboards; it's the only approach that works against arbitrary sites
 * because no JS-detectable signal exists for X-Frame-Options blocks.
 */
const IFRAME_LOAD_TIMEOUT_MS = 4000;

const StandalonePreview: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const websiteUrl = searchParams.get('url');
  const [iframeStatus, setIframeStatus] = useState<'pending' | 'loaded' | 'blocked'>('pending');
  const [isLoading, setIsLoading] = useState(true);
  const [screenshotFailed, setScreenshotFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const widgetLoadedRef = useRef(false);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const maxRetries = 2;

  const widgetUrl = useMemo(() => `${window.location.origin}/widget/widget-loader.js`, []);

  // WordPress mshots - fast, free, reliable. Used as fallback.
  const screenshotUrl = useMemo(() => {
    if (!websiteUrl) return null;
    const base = `https://s.wordpress.com/mshots/v1/${encodeURIComponent(websiteUrl)}?w=1920`;
    return retryCount > 0 ? `${base}&_t=${Date.now()}` : base;
  }, [websiteUrl, retryCount]);

  // Iframe probe: assume blocked if no `load` event fires within timeout.
  // Sites that block via X-Frame-Options never fire `load` (or fire it on
  // an empty document) — the timeout is the only reliable signal.
  useEffect(() => {
    if (!websiteUrl) return;
    setIframeStatus('pending');
    const t = setTimeout(() => {
      setIframeStatus((s) => (s === 'pending' ? 'blocked' : s));
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [websiteUrl]);

  const handleIframeLoad = () => {
    // Cross-origin throw == real cross-origin load == success.
    try {
      const iframe = iframeRef.current;
      if (iframe) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _ = iframe.contentWindow?.location.href;
      }
      setIframeStatus('loaded');
      setIsLoading(false);
    } catch {
      setIframeStatus('loaded');
      setIsLoading(false);
    }
  };

  // Load live widget overlay.
  //
  // The widget loader sets `window.AIChatWidget.initialized = true` once it
  // mounts. We must NOT remove the container on unmount AND keep that flag
  // intact — otherwise a remount (e.g. React StrictMode in dev, or any
  // route-internal re-render) tears the container down but the loader's
  // self-guard exits early on re-run, leaving the page widget-less.
  //
  // Strategy: idempotent mount, no cleanup. The widget owns its DOM and
  // lives for the lifetime of the preview tab.
  useEffect(() => {
    if (!clientId) return;
    if (widgetLoadedRef.current) return;

    // If the page was previously mounted, reuse the existing widget rather
    // than re-injecting the script.
    if (document.getElementById('ai-chat-widget-container')) {
      widgetLoadedRef.current = true;
      return;
    }

    widgetLoadedRef.current = true;

    const script = document.createElement('script');
    script.src = widgetUrl;
    script.async = true;
    script.setAttribute('data-client-id', clientId);
    script.setAttribute('data-brndsh-widget', 'true');
    script.onerror = () => {
      // Surface load failures to the console so we can see them in DevTools.
      // eslint-disable-next-line no-console
      console.error('[StandalonePreview] widget-loader.js failed to load from', widgetUrl);
    };
    document.body.appendChild(script);

    // No cleanup on purpose — see comment above.
  }, [clientId, widgetUrl]);

  const handleScreenshotLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;

    // Check for broken/tiny images
    if (img.naturalWidth < 100 || img.naturalHeight < 100) {
      setScreenshotFailed(true);
      setIsLoading(false);
      return;
    }

    // WordPress placeholder is 400x300 - retry
    if (img.naturalWidth === 400 && img.naturalHeight === 300) {
      if (retryCount < maxRetries) {
        setTimeout(() => setRetryCount(prev => prev + 1), 1500);
        return;
      }
    }

    setIsLoading(false);
  };

  const handleScreenshotError = () => {
    setScreenshotFailed(true);
    setIsLoading(false);
  };

  if (!clientId) {
    return (
      <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: 'white' }}>
        Missing client ID
      </div>
    );
  }

  // No website URL → render a clean "test surface" so the widget is still
  // loaded and chattable. This is the path used by the marketplace preview
  // (QR + compact iframe) where the goal is to TEST the bot, not show a
  // screenshot of an external site.
  if (!websiteUrl) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background:
            'radial-gradient(ellipse at top, #f8fafc 0%, #e2e8f0 100%)',
          padding: '24px',
          textAlign: 'center',
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ maxWidth: 420 }}>
          <div
            style={{
              width: 56,
              height: 56,
              margin: '0 auto 16px',
              borderRadius: 16,
              background: 'linear-gradient(135deg, #4080F2 0%, #6366F1 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h1
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: '#0f172a',
              margin: '0 0 8px',
            }}
          >
            Test your chatbot
          </h1>
          <p
            style={{
              fontSize: 14,
              lineHeight: 1.6,
              color: '#475569',
              margin: '0 0 20px',
            }}
          >
            Click the chat bubble in the corner to start a conversation.
            The bot answers from your knowledge base.
          </p>
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 14px',
              borderRadius: 999,
              background: 'rgba(15, 23, 42, 0.04)',
              fontSize: 12,
              color: '#64748b',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#10b981',
                display: 'inline-block',
              }}
            />
            Live · Chatting won't affect your production bot
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, width: '100vw', height: '100vh', background: '#fff', overflow: 'hidden' }}>
      {/* Loading overlay */}
      {isLoading && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', color: 'white', zIndex: 10 }}>
          <div style={{ width: '60px', height: '60px', position: 'relative', marginBottom: '24px' }}>
            <div style={{ position: 'absolute', inset: 0, border: '3px solid rgba(64, 128, 242, 0.2)', borderRadius: '50%' }} />
            <div style={{ position: 'absolute', inset: 0, border: '3px solid transparent', borderTopColor: '#4080F2', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } } @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }`}</style>
          </div>
          <div style={{ fontSize: '18px', fontWeight: 500, marginBottom: '8px' }}>
            {retryCount > 0 ? `Generating Preview (${retryCount}/${maxRetries})...` : 'Loading Preview...'}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', maxWidth: '300px', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{websiteUrl}</div>
          <div style={{ display: 'flex', gap: '6px', marginTop: '20px' }}>
            {[0, 1, 2].map((i) => <div key={i} style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4080F2', animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite` }} />)}
          </div>
        </div>
      )}

      {/* Layer 1: try interactive iframe of the actual website. */}
      {iframeStatus !== 'blocked' && (
        <iframe
          ref={iframeRef}
          src={websiteUrl}
          onLoad={handleIframeLoad}
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals"
          style={{
            position: 'fixed',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 0,
            zIndex: 1,
            // Hide the iframe entirely until it's confirmed loaded so users
            // don't see a flash of empty white before the screenshot fallback
            // kicks in.
            visibility: iframeStatus === 'loaded' ? 'visible' : 'hidden',
          }}
          title="Website preview"
        />
      )}

      {/* Layer 2: fallback screenshot — used when iframe is blocked OR while
          we're still probing. Never blocks layer 1 once it loads. */}
      {!screenshotFailed && screenshotUrl && iframeStatus !== 'loaded' && (
        <>
          <img
            key={retryCount}
            src={screenshotUrl}
            alt=""
            style={{ display: 'none' }}
            onLoad={handleScreenshotLoad}
            onError={handleScreenshotError}
          />
          {!isLoading && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${screenshotUrl})`,
                backgroundSize: '100% auto',
                backgroundPosition: 'top center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: '#fff',
                zIndex: 1,
              }}
            />
          )}
        </>
      )}

      {/* Subtle "static preview" badge when we're showing the screenshot —
          tells the tester they're not on a live site. */}
      {iframeStatus === 'blocked' && !screenshotFailed && !isLoading && (
        <div
          style={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            padding: '6px 12px',
            borderRadius: 999,
            background: 'rgba(15, 23, 42, 0.75)',
            color: 'white',
            fontSize: 11,
            fontWeight: 500,
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fbbf24' }} />
          Static preview — site blocks interactive embedding
        </div>
      )}

      {/* Fallback UI */}
      {screenshotFailed && (
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', inset: 0, opacity: 0.1, backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
          <div style={{ textAlign: 'center', color: 'white', zIndex: 2, padding: '40px' }}>
            <div style={{ width: '80px', height: '80px', margin: '0 auto 24px', borderRadius: '50%', background: 'rgba(64, 128, 242, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 600, marginBottom: '12px' }}>Widget Preview</h2>
            <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '8px', maxWidth: '400px' }}>
              Preview unavailable for this site.<br />The widget is live and fully functional!
            </p>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0,0,0,0.3)', padding: '10px 16px', borderRadius: '8px', marginTop: '16px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              <span style={{ color: '#94a3b8', fontSize: '13px' }}>{websiteUrl}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StandalonePreview;
